import { Entity } from './entity.js';
import { applyBlueBuff, applyRedBuff } from './jungle/jungleBuffs.js';

export const JUNGLE_CAMPS = {
  blue_golem: { id: 'blue_golem', name: 'Ancient Golem', firstSpawn: 115, respawn: 300, gold: 80, exp: 220 },
  red_lizard: { id: 'red_lizard', name: 'Lizard Elder', firstSpawn: 115, respawn: 300, gold: 80, exp: 220 },
  wraiths: { id: 'wraiths', name: 'Wraiths', firstSpawn: 115, respawn: 50, gold: 35, exp: 95 },
  wolves: { id: 'wolves', name: 'Wolves', firstSpawn: 115, respawn: 50, gold: 40, exp: 110 },
  small_golems: { id: 'small_golems', name: 'Small Golems', firstSpawn: 115, respawn: 50, gold: 45, exp: 120 },
  dragon: { id: 'dragon', name: 'Dragon', firstSpawn: 150, respawn: 360, gold: 190, exp: 400 },
  baron: { id: 'baron', name: 'Baron Nashor', firstSpawn: 900, respawn: 420, gold: 300, exp: 900 }
};

const MONSTER_PROFILES = {
  wolves: [
    { name: 'Giant Wolf', hp: 450, maxHp: 450, ad: 24, armor: 10, mr: 10 },
    { name: 'Small Wolf 1', hp: 260, maxHp: 260, ad: 14, armor: 6, mr: 6 },
    { name: 'Small Wolf 2', hp: 260, maxHp: 260, ad: 14, armor: 6, mr: 6 }
  ],
  wraiths: [
    { name: 'Blue Wraith', hp: 400, maxHp: 400, ad: 35, armor: 15, mr: 15 },
    { name: 'Lesser Wraith 1', hp: 200, maxHp: 200, ad: 10, armor: 5, mr: 5 },
    { name: 'Lesser Wraith 2', hp: 200, maxHp: 200, ad: 10, armor: 5, mr: 5 },
    { name: 'Lesser Wraith 3', hp: 200, maxHp: 200, ad: 10, armor: 5, mr: 5 }
  ],
  small_golems: [
    { name: 'Big Golem', hp: 650, maxHp: 650, ad: 55, armor: 20, mr: 10 },
    { name: 'Small Golem', hp: 300, maxHp: 300, ad: 30, armor: 10, mr: 5 }
  ],
  blue_golem: [
    { name: 'Ancient Golem', hp: 1450, maxHp: 1450, ad: 65, armor: 20, mr: 20 },
    { name: 'Young Lizard 1', hp: 250, maxHp: 250, ad: 12, armor: 8, mr: 8 },
    { name: 'Young Lizard 2', hp: 250, maxHp: 250, ad: 12, armor: 8, mr: 8 }
  ],
  red_lizard: [
    { name: 'Lizard Elder', hp: 1450, maxHp: 1450, ad: 65, armor: 20, mr: 20 },
    { name: 'Young Lizard 1', hp: 250, maxHp: 250, ad: 12, armor: 8, mr: 8 },
    { name: 'Young Lizard 2', hp: 250, maxHp: 250, ad: 12, armor: 8, mr: 8 }
  ],
  dragon: [
    { name: 'Dragon', hp: 3500, maxHp: 3500, ad: 145, armor: 60, mr: 40 }
  ],
  baron: [
    { name: 'Baron Nashor', hp: 8850, maxHp: 8850, ad: 460, armor: 120, mr: 70 }
  ]
};

export function shouldSpawnCamp(campId, currentTime, lastKilledTime) {
  const camp = JUNGLE_CAMPS[campId];
  if (!camp) return false;

  if (lastKilledTime === 0) {
    return currentTime >= camp.firstSpawn;
  }
  return (currentTime - lastKilledTime) >= camp.respawn;
}

export function createJungleCampState(campId, position = { x: 0, z: 0 }) {
  const spec = JUNGLE_CAMPS[campId] || { id: campId, gold: 35, exp: 95 };
  const monstersTemplate = MONSTER_PROFILES[campId] || [{ name: 'Neutral Creep', hp: 300, maxHp: 300, ad: 20 }];

  const totalMonsters = monstersTemplate.length;
  const goldPerMonster = Math.floor(spec.gold / totalMonsters);
  const xpPerMonster = Math.floor(spec.exp / totalMonsters);

  const monsters = monstersTemplate.map((m, idx) => {
    const offsetX = idx === 0 ? 0 : (idx % 2 === 1 ? 1.2 : -1.2);
    const offsetZ = idx === 0 ? 0 : (idx > 2 ? 1.2 : -1.2);

    return new Entity({
      id: `jungle-${campId}-${idx}-${Date.now()}`,
      type: 'jungle',
      team: 'neutral',
      name: m.name,
      x: position.x + offsetX,
      y: 0,
      z: position.z + offsetZ,
      hp: m.hp,
      maxHp: m.maxHp,
      ad: m.ad,
      armor: m.armor || 10,
      mr: m.mr || 10,
      radius: idx === 0 ? 24 : 16,
      attackRange: 120,
      bounty: goldPerMonster,
      exp: xpPerMonster
    });
  });

  return {
    id: campId,
    spec,
    position,
    isAlive: true,
    lastKilledTime: 0,
    monsters,
    currentTarget: null
  };
}

export function checkCampClearedStatus(campState, currentTime = 0) {
  if (!campState || !campState.monsters) return true;

  const allDead = campState.monsters.every(m => m.isDead || m.hp <= 0);
  if (allDead && campState.isAlive) {
    campState.isAlive = false;
    campState.lastKilledTime = currentTime;
    return true;
  }
  return !campState.isAlive;
}

export function processCampKill(campState, killerEntity, currentTime) {
  if (!campState || !campState.isAlive) {
    return { success: false, reason: 'CAMP_ALREADY_DEAD' };
  }

  campState.isAlive = false;
  campState.lastKilledTime = currentTime;

  campState.monsters.forEach(m => {
    m.isDead = true;
    m.hp = 0;
  });

  const goldReward = campState.spec.gold || 35;
  const xpReward = campState.spec.exp || 95;

  if (killerEntity) {
    killerEntity.gold = (killerEntity.gold || 0) + goldReward;
    killerEntity.xp = (killerEntity.xp || 0) + xpReward;

    if (campState.id === 'blue_golem') {
      applyBlueBuff(killerEntity, 150);
    } else if (campState.id === 'red_lizard') {
      applyRedBuff(killerEntity, 150);
    }
  }

  return {
    success: true,
    goldGranted: goldReward,
    xpGranted: xpReward
  };
}

export function updateCampLeashAggro(campState, candidateAttackers) {
  if (!candidateAttackers || candidateAttackers.length === 0) {
    campState.currentTarget = null;
    return null;
  }

  const cx = campState.position.x;
  const cz = campState.position.z;

  let closest = null;
  let minDistanceSq = Infinity;

  candidateAttackers.forEach(attacker => {
    if (!attacker.isDead) {
      const dx = attacker.x - cx;
      const dz = (attacker.z !== undefined ? attacker.z : attacker.y || 0) - cz;
      const distSq = dx * dx + dz * dz;

      if (distSq < minDistanceSq) {
        minDistanceSq = distSq;
        closest = attacker;
      }
    }
  });

  campState.currentTarget = closest;
  return closest;
}

export function getScaledDragonStats(gameTimeMinutes = 0) {
  const hp = 3500 + Math.floor(240 * gameTimeMinutes);
  const ad = 145 + Math.floor(4 * gameTimeMinutes);
  return { hp, maxHp: hp, ad, armor: 60, mr: 40 };
}

export function getScaledBaronStats(gameTimeMinutes = 0) {
  const hp = 8850 + Math.floor(140 * gameTimeMinutes);
  const ad = 460;
  return { hp, maxHp: hp, ad, armor: 120, mr: 70 };
}

export {
  BARON_BUFF_SPEC,
  BLUE_BUFF_SPEC,
  RED_BUFF_SPEC,
  applyBaronBuff,
  applyBlueBuff,
  applyRedBuff,
  onHitRedBuff,
  updateBuffs,
  transferBuffsOnKill,
  processMajorObjectiveKill
} from './jungle/jungleBuffs.js';




